/**
 * nlParseService - API service for NL workflow parsing (T7.2)
 *
 * Calls POST /api/agents/workflow-nl-parse. Falls back to keyword-match
 * heuristic when the endpoint is unavailable.
 *
 * Patent: Natural Language Workflow Builder for AI-Governed Systems
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import { Make_Authorized_API_Call, isSuccessResponse } from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../services/api/endpoints';
import type { NLParseRequest, NLParseResponse, ParsedStep, ParsedWorkflow } from './types';

/**
 * Parse a natural language description into structured workflow steps.
 * Falls back to keyword matching if the API endpoint is unavailable.
 */
export async function parseNaturalLanguage(request: NLParseRequest): Promise<NLParseResponse> {
  try {
    const response = await Make_Authorized_API_Call<NLParseResponse>(
      API_ENDPOINTS.AGENTS.NL_PARSE,
      'POST',
      request
    );

    if (isSuccessResponse(response)) {
      const record = response.data.Records[0];
      if (record) return record;
    }

    // API returned non-success — fall back to keyword matching
    console.warn('[nlParseService] API unavailable, using keyword fallback');
    return keywordFallback(request);
  } catch (err) {
    console.warn('[nlParseService] API call failed, using keyword fallback:', err);
    return keywordFallback(request);
  }
}

/**
 * Keyword-match fallback for offline/error scenarios.
 * Splits the description into sentences, matches each against available agents
 * by keyword overlap.
 */
function keywordFallback(request: NLParseRequest): NLParseResponse {
  const { description, availableAgents } = request;

  // Split into actionable sentences
  const sentences = description
    .split(/[.!?\n]+/)
    .map(s => s.trim())
    .filter(s => s.length > 5);

  if (sentences.length === 0) {
    return {
      success: false,
      workflow: {
        name: 'Untitled Workflow',
        description,
        steps: [],
        estimatedComplexity: 'low',
        warnings: ['Could not parse any actionable steps from the description.'],
      },
      parseConfidence: 0,
    };
  }

  const steps: ParsedStep[] = sentences.map((sentence, idx) => {
    const words = sentence.toLowerCase().split(/\s+/);

    // Score each agent by keyword overlap
    const scored = availableAgents.map(agent => {
      const agentWords = [
        ...agent.name.toLowerCase().split(/[\s-_]+/),
        ...agent.category.toLowerCase().split(/[\s-_]+/),
        ...(agent.description || '').toLowerCase().split(/\s+/),
      ];

      let matches = 0;
      for (const word of words) {
        if (word.length < 3) continue;
        if (agentWords.some(aw => aw.includes(word) || word.includes(aw))) {
          matches++;
        }
      }

      const confidence = Math.min(1, matches / Math.max(words.length * 0.3, 1));
      return { agentId: agent.id, agentName: agent.name, confidence };
    });

    scored.sort((a, b) => b.confidence - a.confidence);
    const bestMatch = scored[0];
    const alternatives = scored.slice(1, 4).filter(s => s.confidence > 0.1);

    return {
      order: idx + 1,
      description: sentence,
      matchedAgentId: bestMatch?.confidence > 0.15 ? bestMatch.agentId : undefined,
      matchedAgentName: bestMatch?.confidence > 0.15 ? bestMatch.agentName : undefined,
      confidence: bestMatch?.confidence || 0,
      suggestedAlternatives: alternatives,
    };
  });

  const avgConfidence = steps.reduce((sum, s) => sum + s.confidence, 0) / steps.length;
  const warnings: string[] = [];
  const unmatchedCount = steps.filter(s => !s.matchedAgentId).length;
  if (unmatchedCount > 0) {
    warnings.push(`${unmatchedCount} step(s) could not be matched to an agent. Please assign manually.`);
  }
  if (avgConfidence < 0.3) {
    warnings.push('Low overall confidence. Consider rephrasing your description with more specific agent/task names.');
  }

  return {
    success: true,
    workflow: {
      name: sentences[0].slice(0, 50) + (sentences[0].length > 50 ? '...' : ''),
      description,
      steps,
      estimatedComplexity: steps.length <= 3 ? 'low' : steps.length <= 6 ? 'medium' : 'high',
      warnings,
    },
    parseConfidence: avgConfidence,
  };
}
